import { ReactNode } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS, Transform } from '@dnd-kit/utilities';
import { css } from '@emotion/react';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import type { DraggedEventPosition } from '../../../hooks/scheduler-v3/use-appointment-drag-drop';
import { EVENT_CARD_COLOR_LINE_WIDTH } from '../constants';
import { zIndex } from '../utils';

type Props = {
  children: ReactNode;
  eventId: string;
  eventPosition?: DraggedEventPosition;
  isAppointment: boolean;
  sourceColumnId?: string;
  isDraggingCurrentCard?: boolean;
};

export const DraggableEventCardWrapper = ({
  children,
  eventId,
  eventPosition,
  isAppointment,
  sourceColumnId,
  isDraggingCurrentCard = false,
}: Props) => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();

  // Enable drag only for v3 core office
  const isAppointmentReorderingEnabled = isScheduleV3FlagEnabled && !isIntegratedOffice;

  const { attributes, isDragging, listeners, transform, setNodeRef } = useDraggable({
    id: eventId,
    data: { sourceColumnId, eventPosition },
    disabled: !isAppointment || !isAppointmentReorderingEnabled,
  });

  if (!isAppointment || !isAppointmentReorderingEnabled || (isDragging && !isDraggingCurrentCard)) {
    return <div css={{ height: '100%' }}>{children}</div>;
  }

  return (
    <div ref={setNodeRef} css={containerStyle(transform, isDragging)}>
      <div
        className='event-card-drag-handle'
        css={dragIconContainerStyle}
        {...listeners}
        {...attributes}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Icon name='dragand-drop-handle' color='default' size={16} css={dragIconStyle} />
      </div>
      {children}
    </div>
  );
};

const containerStyle = (transform: Transform | null, isDragging: boolean) =>
  css({
    transform: transform ? `${CSS.Translate.toString(transform)} rotate(${isDragging ? '-10deg' : '0deg'})` : undefined,
    transition: 'transform 0.2s',
    width: '100%',
    height: '100%',
  });

const dragIconContainerStyle = css({
  display: 'none',
  position: 'absolute',
  top: '50%',
  left: 2 * EVENT_CARD_COLOR_LINE_WIDTH,
  transform: 'translate(-100%, -50%)',
  padding: EVENT_CARD_COLOR_LINE_WIDTH,
  zIndex: zIndex.eventCardDefault,
  cursor: 'grab',
});

const dragIconStyle = css({
  background: theme.colors.white,
  boxShadow: `${theme.shadows.light}`,
  borderRadius: '2px',
});
